import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== CONTEXT ==============
const AuthContext = React.createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("token", authToken);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => React.useContext(AuthContext);

// ============== LANDING PAGE ==============
const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="landing-page" data-testid="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="badge" data-testid="badge">PW FAMILY OFFICE</div>
        <h1 className="hero-title">
          Building the next wave of<br />
          <span className="gold-text">Indian startups</span>
        </h1>
        <p className="hero-subtitle">
          We built Physics Wallah from scratch to India's most valued EdTech unicorn. Now,<br />
          we're backing founders who dare to dream big.
        </p>
        <button 
          className="cta-button" 
          onClick={() => navigate("/apply")}
          data-testid="apply-now-btn"
        >
          APPLY NOW <span className="arrow">›</span>
        </button>
        <p className="time-note">Takes less than 2 minutes</p>
      </section>

      <div className="divider"></div>

      {/* Founders Section */}
      <section className="founders-section">
        <p className="section-label">MEET THE FOUNDERS</p>
        <h2 className="section-title">Built by entrepreneurs,<br />for entrepreneurs</h2>
        
        <div className="founders-grid">
          <div className="founder-card olive-border">
            <div className="founder-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_app-forge-1278/artifacts/bhnydjui_image.png" 
                alt="Alakh Pandey"
                className="founder-image"
              />
            </div>
            <h3 className="founder-name">Alakh Pandey</h3>
            <p className="founder-role">Founder & CEO</p>
            <div className="founder-line"></div>
            <p className="founder-desc">Built India's most loved EdTech from a YouTube channel to a $1B+ unicorn</p>
          </div>
          
          <div className="founder-card gold-border">
            <div className="founder-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_app-forge-1278/artifacts/xjz5c4u5_image.png" 
                alt="Prateek Maheshwari"
                className="founder-image"
              />
            </div>
            <h3 className="founder-name">Prateek Maheshwari</h3>
            <p className="founder-role">Co-Founder</p>
            <div className="founder-line gold"></div>
            <p className="founder-desc">Scaled PW from 0 to 50M+ students and 10,000+ team members</p>
          </div>
        </div>
      </section>

      <div className="divider"></div>

      {/* Values Section */}
      <section className="values-section">
        <p className="section-label">WHAT WE BELIEVE IN</p>
        <h2 className="section-title">The founders we back</h2>
        
        <div className="values-grid">
          <div className="value-card olive">
            <div className="value-icon">✦</div>
            <h3 className="value-title">Bold Ideas</h3>
            <p className="value-desc">Solving real problems for real people</p>
          </div>
          
          <div className="value-card teal">
            <div className="value-icon">⚡</div>
            <h3 className="value-title">Obsessed Founders</h3>
            <p className="value-desc">Relentlessly focused on their customers</p>
          </div>
          
          <div className="value-card navy">
            <div className="value-icon">☆</div>
            <h3 className="value-title">Long-term Vision</h3>
            <p className="value-desc">Building sustainable businesses, not quick flips</p>
          </div>
        </div>
      </section>

      <div className="divider"></div>

      {/* Quote Section */}
      <section className="quote-section">
        <p className="quote-text">
          "We've been in your shoes. We know the sleepless nights, the pivots, the moments of doubt.<br />
          We're not just investors — we're founders backing founders."
        </p>
        <p className="quote-author">— Alakh & Prateek</p>
      </section>

      <div className="divider"></div>

      {/* CTA Section */}
      <section className="cta-section">
        <h2 className="cta-title">Ready to build something extraordinary?</h2>
        <button 
          className="cta-button-outline" 
          onClick={() => navigate("/apply")}
          data-testid="apply-now-btn-bottom"
        >
          Apply Now <span className="arrow">→</span>
        </button>
      </section>
    </div>
  );
};

// ============== CITIES LIST ==============
const INDIAN_CITIES = [
  "Mumbai", "Delhi", "Bangalore", "Bengaluru", "Hyderabad", "Chennai", "Kolkata", "Pune", "Ahmedabad", "Jaipur",
  "Surat", "Lucknow", "Kanpur", "Nagpur", "Indore", "Thane", "Bhopal", "Visakhapatnam", "Patna", "Vadodara",
  "Ghaziabad", "Ludhiana", "Agra", "Nashik", "Faridabad", "Meerut", "Rajkot", "Varanasi", "Srinagar", "Aurangabad",
  "Dhanbad", "Amritsar", "Navi Mumbai", "Allahabad", "Ranchi", "Howrah", "Coimbatore", "Jabalpur", "Gwalior", "Vijayawada",
  "Jodhpur", "Madurai", "Raipur", "Kota", "Chandigarh", "Guwahati", "Solapur", "Hubli", "Tiruchirappalli", "Bareilly",
  "Mysore", "Tiruppur", "Gurgaon", "Gurugram", "Aligarh", "Jalandhar", "Bhubaneswar", "Salem", "Warangal", "Guntur",
  "Bhiwandi", "Saharanpur", "Gorakhpur", "Bikaner", "Amravati", "Noida", "Jamshedpur", "Bhilai", "Cuttack", "Firozabad",
  "Kochi", "Nellore", "Bhavnagar", "Dehradun", "Durgapur", "Asansol", "Rourkela", "Nanded", "Kolhapur", "Ajmer",
  "Akola", "Gulbarga", "Jamnagar", "Ujjain", "Loni", "Siliguri", "Jhansi", "Ulhasnagar", "Jammu", "Sangli",
  "Mangalore", "Erode", "Belgaum", "Kurnool", "Ambattur", "Rajahmundry", "Tirunelveli", "Malegaon", "Gaya", "Udaipur"
];

// Helper function to get initial form data
const getInitialFormData = () => ({
  startup_name: "",
  first_name: "",
  last_name: "",
  phone_code: "+91",
  phone_number: "",
  email: "",
  city: "",
  linkedin_url: "",
  start_month: "",
  start_year: "",
  num_founders: "",
  cofounder_names: ["", "", ""],
  cofounder_linkedins: ["", "", ""],
  elevator_pitch: "",
  demo_links: [""],
  demo_files: [],
  sectors: [],
  startup_stage: "",
  fundraise_amount: "",
  fundraise_currency: "INR",
  fundraise_stage: "",
  pitch_deck_url: "",
  pitch_deck_name: "",
  referral_source: ""
});

// ============== APPLICATION FORM ==============
const ApplicationForm = () => {
  const navigate = useNavigate();
  const totalSteps = 12;
  
  // Load saved step from localStorage
  const [step, setStep] = useState(() => {
    const saved = localStorage.getItem('pw_form_step');
    return saved ? parseInt(saved) : 1;
  });
  
  // Load saved form data from localStorage
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('pw_form_data');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return getInitialFormData();
      }
    }
    return getInitialFormData();
  });

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [validationMessage, setValidationMessage] = useState("");
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('pw_form_data', JSON.stringify(formData));
  }, [formData]);

  // Save step to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('pw_form_step', step.toString());
  }, [step]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
    setValidationMessage("");
    
    // Handle city autocomplete
    if (field === "city") {
      if (value.length > 0) {
        const filtered = INDIAN_CITIES.filter(city => 
          city.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredCities(filtered.slice(0, 8));
        setShowCitySuggestions(true);
      } else {
        setFilteredCities([]);
        setShowCitySuggestions(false);
      }
    }
  };

  const selectCity = (city) => {
    setFormData(prev => ({ ...prev, city }));
    setShowCitySuggestions(false);
    setFilteredCities([]);
  };

  const updateCofounderLinkedin = (index, value) => {
    const newLinkedins = [...formData.cofounder_linkedins];
    newLinkedins[index] = value;
    setFormData(prev => ({ ...prev, cofounder_linkedins: newLinkedins }));
    setErrors(prev => ({ ...prev, [`cofounder_linkedin_${index}`]: null }));
    setValidationMessage("");
  };

  const updateCofounderName = (index, value) => {
    const newNames = [...(formData.cofounder_names || ["", "", ""])];
    newNames[index] = value;
    setFormData(prev => ({ ...prev, cofounder_names: newNames }));
    setErrors(prev => ({ ...prev, [`cofounder_name_${index}`]: null }));
    setValidationMessage("");
  };

  const addDemoLink = () => {
    setFormData(prev => ({
      ...prev,
      demo_links: [...prev.demo_links, ""]
    }));
  };

  const updateDemoLink = (index, value) => {
    const newLinks = [...formData.demo_links];
    newLinks[index] = value;
    setFormData(prev => ({ ...prev, demo_links: newLinks }));
  };

  const removeDemoLink = (index) => {
    setFormData(prev => ({
      ...prev,
      demo_links: prev.demo_links.filter((_, i) => i !== index)
    }));
  };

  const handleDemoFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    
    const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    try {
      // For small files (< 5MB), use regular upload
      if (file.size < 5 * 1024 * 1024) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        
        const response = await axios.post(`${API}/upload`, formDataUpload, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        });
        
        setFormData(prev => ({
          ...prev,
          demo_files: [...(prev.demo_files || []), {
            url: response.data.url,
            filename: response.data.filename
          }]
        }));
      } else {
        // For large files, use chunked upload
        const initResponse = await axios.post(`${API}/chunked-upload/init`, {
          filename: file.name,
          total_size: file.size,
          total_chunks: totalChunks
        });
        
        const uploadId = initResponse.data.upload_id;
        
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          
          const chunkFormData = new FormData();
          chunkFormData.append('file', chunk);
          
          await axios.post(`${API}/chunked-upload/${uploadId}/chunk/${i}`, chunkFormData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          
          const percent = Math.round(((i + 1) / totalChunks) * 100);
          setUploadProgress(percent);
        }
        
        const completeResponse = await axios.post(`${API}/chunked-upload/${uploadId}/complete`);
        
        setFormData(prev => ({
          ...prev,
          demo_files: [...(prev.demo_files || []), {
            url: completeResponse.data.url,
            filename: completeResponse.data.filename
          }]
        }));
      }
    } catch (error) {
      console.error("Demo file upload failed:", error);
      alert("Failed to upload file. Please try again.");
    }
    setUploading(false);
    setUploadProgress(0);
    e.target.value = '';
  };

  const removeDemoFile = (index) => {
    setFormData(prev => ({
      ...prev,
      demo_files: prev.demo_files.filter((_, i) => i !== index)
    }));
  };

  const toggleSector = (sector) => {
    setFormData(prev => ({
      ...prev,
      sectors: prev.sectors.includes(sector)
        ? prev.sectors.filter(s => s !== sector)
        : [...prev.sectors, sector]
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    
    const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    try {
      // For small files (< 5MB), use regular upload
      if (file.size < 5 * 1024 * 1024) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        
        const response = await axios.post(`${API}/upload`, formDataUpload, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        });
        
        updateField("pitch_deck_url", response.data.url);
        updateField("pitch_deck_name", response.data.filename);
      } else {
        // For large files, use chunked upload (public endpoint)
        // Step 1: Initialize upload
        const initResponse = await axios.post(`${API}/chunked-upload/init`, {
          filename: file.name,
          total_size: file.size,
          total_chunks: totalChunks
        });
        
        const uploadId = initResponse.data.upload_id;
        
        // Step 2: Upload chunks
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          
          const chunkFormData = new FormData();
          chunkFormData.append('file', chunk);
          
          await axios.post(`${API}/chunked-upload/${uploadId}/chunk/${i}`, chunkFormData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          
          // Update progress
          const percent = Math.round(((i + 1) / totalChunks) * 100);
          setUploadProgress(percent);
        }
        
        // Step 3: Complete upload
        const completeResponse = await axios.post(`${API}/chunked-upload/${uploadId}/complete`);
        
        updateField("pitch_deck_url", completeResponse.data.url);
        updateField("pitch_deck_name", completeResponse.data.filename);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Failed to upload file. Please try again.");
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const validateStep = () => {
    const newErrors = {};
    let message = "";
    
    switch(step) {
      case 1:
        if (!formData.startup_name.trim()) {
          newErrors.startup_name = "Required";
          message = "Please enter your startup name to continue";
        }
        break;
      case 2:
        if (!formData.first_name.trim()) {
          newErrors.first_name = "Required";
          message = "Please fill in all required fields";
        }
        if (!formData.last_name.trim()) {
          newErrors.last_name = "Required";
          message = "Please fill in all required fields";
        }
        if (!formData.phone_number.trim()) {
          newErrors.phone_number = "Required";
          message = "Please fill in all required fields";
        }
        if (!formData.email.trim()) {
          newErrors.email = "Required";
          message = "Please fill in all required fields";
        }
        break;
      case 3:
        if (!formData.city.trim()) {
          newErrors.city = "Required";
          message = "Please enter your city to continue";
        }
        break;
      case 4:
        if (!formData.linkedin_url.trim()) {
          newErrors.linkedin_url = "Required";
          message = "Please share your LinkedIn profile to continue";
        }
        break;
      case 5:
        if (!formData.start_month) {
          newErrors.start_month = "Required";
          message = "Please select when you started working on your startup";
        }
        if (!formData.start_year) {
          newErrors.start_year = "Required";
          message = "Please select when you started working on your startup";
        }
        break;
      case 6:
        if (!formData.num_founders) {
          newErrors.num_founders = "Required";
          message = "Please select the number of founders";
        }
        // Validate co-founder names and LinkedIns if more than 1 founder
        if (formData.num_founders && formData.num_founders !== "1") {
          const numCofounders = formData.num_founders === "4+" ? 3 : parseInt(formData.num_founders) - 1;
          for (let i = 0; i < numCofounders; i++) {
            if (!formData.cofounder_names?.[i]?.trim()) {
              newErrors[`cofounder_name_${i}`] = "Required";
              message = "Please provide names for all co-founders";
            }
            if (!formData.cofounder_linkedins[i]?.trim()) {
              newErrors[`cofounder_linkedin_${i}`] = "Required";
              message = "Please provide LinkedIn profiles for all co-founders";
            }
          }
        }
        break;
      case 7:
        if (formData.sectors.length === 0) {
          newErrors.sectors = "Select at least one";
          message = "Please select at least one sector";
        }
        break;
      case 8:
        if (!formData.startup_stage) {
          newErrors.startup_stage = "Required";
          message = "Please select your startup's current stage";
        }
        break;
      case 9:
        if (!formData.elevator_pitch.trim()) {
          newErrors.elevator_pitch = "Required";
          message = "Please share your elevator pitch to continue";
        }
        break;
      case 10:
        // Optional - supporting materials
        break;
      case 11:
        if (!formData.fundraise_amount.trim()) {
          newErrors.fundraise_amount = "Required";
          message = "Please fill in all fundraising details";
        }
        if (!formData.fundraise_stage) {
          newErrors.fundraise_stage = "Required";
          message = "Please fill in all fundraising details";
        }
        break;
      case 12:
        if (!formData.referral_source.trim()) {
          newErrors.referral_source = "Required";
          message = "Please let us know how you heard about us";
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    setValidationMessage(message);
    return Object.keys(newErrors).length === 0;
  };

  const handleBack = (e) => {
    e.preventDefault();
    if (step > 1) {
      setStep(step - 1);
    } else {
      // Clear form data when going back to home
      localStorage.removeItem('pw_form_data');
      localStorage.removeItem('pw_form_step');
      navigate("/");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        demo_links: formData.demo_links.filter(link => link.trim())
      };
      
      await axios.post(`${API}/applications`, submitData);
      
      // Clear form data from localStorage after successful submission
      localStorage.removeItem('pw_form_data');
      localStorage.removeItem('pw_form_step');
      
      navigate("/success");
    } catch (error) {
      console.error("Submit failed:", error);
      alert("Failed to submit application. Please try again.");
    }
    setSubmitting(false);
  };

  const handleContinue = (e) => {
    e.preventDefault();
    if (validateStep()) {
      if (step < totalSteps) {
        setStep(step + 1);
      }
    }
  };

  const wordCount = formData.elevator_pitch.trim().split(/\s+/).filter(w => w).length;

  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const years = Array.from({ length: 30 }, (_, i) => (new Date().getFullYear() - i).toString());
  
  const sectors = ["AI", "Deep Tech", "B2C Consumer Tech", "SaaS & Enterprise", "D2C Consumer", "Manufacturing", "Fintech", "Healthcare", "Edtech", "Climate & Cleantech", "Foodtech", "Logistics", "E-Com/Q-Com", "Others"];
  
  const stages = [
    "Still on paper (napkin stage)",
    "We have a working prototype",
    "Our first few customers are using it",
    "Revenue is flowing steadily"
  ];
  
  const fundraiseStages = [
    "Just getting started — no commitments yet",
    "We already have a lead investor on board",
    "A few minor commitments, but no lead yet"
  ];

  const renderStep = () => {
    switch(step) {
      case 1:
        return (
          <div className="form-step" data-testid="step-1">
            <h2 className="step-title">What's the name of your startup? <span className="required">*</span></h2>
            <p className="step-subtitle">Every great startup starts with a name. What's yours?</p>
            <input
              type="text"
              className={`form-input ${errors.startup_name ? 'error' : ''}`}
              placeholder="e.g. Acme Technologies, FreshKart, EduSpark..."
              value={formData.startup_name}
              onChange={(e) => updateField("startup_name", e.target.value)}
              data-testid="startup-name-input"
            />
          </div>
        );
      
      case 2:
        return (
          <div className="form-step" data-testid="step-2">
            <h2 className="step-title">Tell us a bit about yourself <span className="required">*</span></h2>
            <p className="step-subtitle">We'd love to know who's behind this venture.</p>
            
            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  className={`form-input ${errors.first_name ? 'error' : ''}`}
                  placeholder="Your first name"
                  value={formData.first_name}
                  onChange={(e) => updateField("first_name", e.target.value)}
                  data-testid="first-name-input"
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  className={`form-input ${errors.last_name ? 'error' : ''}`}
                  placeholder="Your last name"
                  value={formData.last_name}
                  onChange={(e) => updateField("last_name", e.target.value)}
                  data-testid="last-name-input"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Phone Number</label>
              <div className="phone-input-group">
                <select 
                  className="phone-code-select"
                  value={formData.phone_code}
                  onChange={(e) => updateField("phone_code", e.target.value)}
                >
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+44">🇬🇧 +44</option>
                </select>
                <input
                  type="tel"
                  className={`form-input phone-input ${errors.phone_number ? 'error' : ''}`}
                  placeholder="98765 43210"
                  value={formData.phone_number}
                  onChange={(e) => updateField("phone_number", e.target.value)}
                  data-testid="phone-input"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="your.best@email.com"
                value={formData.email}
                onChange={(e) => updateField("email", e.target.value)}
                data-testid="email-input"
              />
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="form-step" data-testid="step-3">
            <h2 className="step-title">Where are you based out of? <span className="required">*</span></h2>
            <p className="step-subtitle">Help us place you on the map.</p>
            <div className="city-autocomplete">
              <input
                type="text"
                className={`form-input ${errors.city ? 'error' : ''}`}
                placeholder="Start typing your city..."
                value={formData.city}
                onChange={(e) => updateField("city", e.target.value)}
                onFocus={() => {
                  if (formData.city.length > 0) {
                    const filtered = INDIAN_CITIES.filter(city => 
                      city.toLowerCase().includes(formData.city.toLowerCase())
                    );
                    setFilteredCities(filtered.slice(0, 8));
                    setShowCitySuggestions(true);
                  }
                }}
                onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                data-testid="city-input"
              />
              {showCitySuggestions && filteredCities.length > 0 && (
                <div className="city-suggestions" data-testid="city-suggestions">
                  {filteredCities.map((city, idx) => (
                    <div 
                      key={idx} 
                      className="city-suggestion-item"
                      onClick={() => selectCity(city)}
                      data-testid={`city-option-${city}`}
                    >
                      {city}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="city-hint">Select from suggestions or type your city if not listed</p>
          </div>
        );
      
      case 4:
        return (
          <div className="form-step" data-testid="step-4">
            <h2 className="step-title">Please share your LinkedIn profile <span className="required">*</span></h2>
            <p className="step-subtitle">We promise we'll only use it for professional stalking.</p>
            <input
              type="url"
              className={`form-input ${errors.linkedin_url ? 'error' : ''}`}
              placeholder="Paste your LinkedIn profile URL here"
              value={formData.linkedin_url}
              onChange={(e) => updateField("linkedin_url", e.target.value)}
              data-testid="linkedin-input"
            />
          </div>
        );
      
      case 5:
        return (
          <div className="form-step" data-testid="step-5">
            <h2 className="step-title">When did you start working on this startup actively? <span className="required">*</span></h2>
            <p className="step-subtitle">Think back to when the idea first started taking shape — not the incorporation date.</p>
            
            <div className="form-row">
              <select
                className={`form-select ${errors.start_month ? 'error' : ''}`}
                value={formData.start_month}
                onChange={(e) => updateField("start_month", e.target.value)}
                data-testid="month-select"
              >
                <option value="">Month</option>
                {months.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              
              <select
                className={`form-select ${errors.start_year ? 'error' : ''}`}
                value={formData.start_year}
                onChange={(e) => updateField("start_year", e.target.value)}
                data-testid="year-select"
              >
                <option value="">Year</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        );
      
      case 6:
        const numCofounders = formData.num_founders === "4+" ? 3 : 
                             formData.num_founders ? parseInt(formData.num_founders) - 1 : 0;
        return (
          <div className="form-step" data-testid="step-6">
            <h2 className="step-title">Total number of founders <span className="required">*</span></h2>
            <p className="step-subtitle">Including yourself</p>
            
            <div className="option-pills">
              {["1", "2", "3", "4+"].map(num => (
                <button
                  key={num}
                  type="button"
                  className={`option-pill ${formData.num_founders === num ? 'selected' : ''}`}
                  onClick={() => updateField("num_founders", num)}
                  data-testid={`founders-${num}`}
                >
                  {num}
                </button>
              ))}
            </div>

            {formData.num_founders && formData.num_founders !== "1" && (
              <div className="cofounder-section">
                <h3 className="cofounder-title">Co-founder Details <span className="required">*</span></h3>
                <p className="cofounder-hint">Please provide details for your other co-founders</p>
                
                {Array.from({ length: numCofounders }, (_, idx) => (
                  <div key={idx} className="cofounder-card">
                    <div className="cofounder-card-header">Founder {idx + 2}</div>
                    <div className="form-group">
                      <label>Full Name</label>
                      <input
                        type="text"
                        className={`form-input ${errors[`cofounder_name_${idx}`] ? 'error' : ''}`}
                        placeholder="Co-founder's full name"
                        value={formData.cofounder_names?.[idx] || ""}
                        onChange={(e) => updateCofounderName(idx, e.target.value)}
                        data-testid={`cofounder-name-${idx}`}
                      />
                    </div>
                    <div className="form-group">
                      <label>LinkedIn Profile</label>
                      <input
                        type="url"
                        className={`form-input ${errors[`cofounder_linkedin_${idx}`] ? 'error' : ''}`}
                        placeholder="https://linkedin.com/in/cofounder"
                        value={formData.cofounder_linkedins[idx] || ""}
                        onChange={(e) => updateCofounderLinkedin(idx, e.target.value)}
                        data-testid={`cofounder-linkedin-${idx}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      
      case 7:
        return (
          <div className="form-step" data-testid="step-7">
            <h2 className="step-title">Which sectors does your startup fall under? <span className="required">*</span></h2>
            <p className="step-subtitle">You can choose multiple if your startup spans across sectors.</p>
            
            <div className="sector-pills">
              {sectors.map(sector => (
                <button
                  key={sector}
                  type="button"
                  className={`sector-pill ${formData.sectors.includes(sector) ? 'selected' : ''}`}
                  onClick={() => toggleSector(sector)}
                  data-testid={`sector-${sector}`}
                >
                  {sector}
                </button>
              ))}
            </div>
          </div>
        );
      
      case 8:
        return (
          <div className="form-step" data-testid="step-8">
            <h2 className="step-title">What is the current stage of your startup? <span className="required">*</span></h2>
            <p className="step-subtitle">Every stage has its own magic. Where are you in the journey?</p>
            
            <div className="stage-options">
              {stages.map(stage => (
                <button
                  key={stage}
                  type="button"
                  className={`stage-option ${formData.startup_stage === stage ? 'selected' : ''}`}
                  onClick={() => updateField("startup_stage", stage)}
                  data-testid={`stage-${stage.substring(0, 10)}`}
                >
                  {stage}
                </button>
              ))}
            </div>
          </div>
        );
      
      case 9:
        return (
          <div className="form-step" data-testid="step-9">
            <h2 className="step-title">Your elevator pitch <span className="required">*</span></h2>
            <p className="step-subtitle">100 words to tell us what you're building and why it matters.</p>
            
            <textarea
              className={`form-textarea ${errors.elevator_pitch ? 'error' : ''}`}
              placeholder="What's broken in the world, and how are you fixing it?"
              value={formData.elevator_pitch}
              onChange={(e) => updateField("elevator_pitch", e.target.value)}
              rows={5}
              data-testid="pitch-textarea"
            />
            <p className="word-count">{wordCount}/100 words</p>
          </div>
        );
      
      case 10:
        return (
          <div className="form-step" data-testid="step-10">
            <h2 className="step-title">Help us understand your business better</h2>
            <p className="step-subtitle">Share any materials that showcase your product, vision, or traction. This is optional but highly encouraged.</p>
            
            {/* File Upload Section */}
            <div className="materials-section">
              <label className="materials-label">📎 Attach Files</label>
              <p className="materials-hint">Pitch decks, product screenshots, demo videos, business plans — anything that tells your story</p>
              
              <div className="file-upload-compact">
                <input
                  type="file"
                  id="materials-file"
                  accept="image/*,video/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                  onChange={handleDemoFileUpload}
                  style={{ display: 'none' }}
                  data-testid="materials-file-input"
                />
                <label htmlFor="materials-file" className="file-upload-btn-compact">
                  {uploading ? (uploadProgress > 0 ? `Uploading ${uploadProgress}%` : "Uploading...") : "+ Add file"}
                </label>
                {uploading && uploadProgress > 0 && (
                  <div className="upload-progress-bar" style={{marginTop: '8px'}}>
                    <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                )}
              </div>

              {formData.demo_files && formData.demo_files.length > 0 && (
                <div className="uploaded-files-list">
                  {formData.demo_files.map((file, idx) => (
                    <div key={idx} className="uploaded-file-item" data-testid={`material-file-${idx}`}>
                      <span className="file-icon">📄</span>
                      <span className="file-name">{file.filename}</span>
                      <button 
                        type="button" 
                        className="remove-item-btn"
                        onClick={() => removeDemoFile(idx)}
                        data-testid={`remove-file-${idx}`}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Links Section */}
            <div className="materials-section">
              <label className="materials-label">🔗 Add Links</label>
              <p className="materials-hint">Website, app store links, demo videos, product walkthroughs, press coverage</p>
              
              {formData.demo_links.map((link, idx) => (
                <div key={idx} className="link-input-row">
                  <input
                    type="url"
                    className="form-input"
                    placeholder="https://..."
                    value={link}
                    onChange={(e) => updateDemoLink(idx, e.target.value)}
                    data-testid={`material-link-${idx}`}
                  />
                  {formData.demo_links.length > 1 && (
                    <button 
                      type="button" 
                      className="remove-item-btn"
                      onClick={() => removeDemoLink(idx)}
                      data-testid={`remove-link-${idx}`}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              
              <button 
                type="button" 
                className="add-item-btn"
                onClick={addDemoLink}
                data-testid="add-link-btn"
              >
                + Add another link
              </button>
            </div>

            <p className="skip-note">Nothing ready yet? No problem — just hit Continue to skip.</p>
          </div>
        );
      
      case 11:
        return (
          <div className="form-step" data-testid="step-11">
            <h2 className="step-title">Fundraising details <span className="required">*</span></h2>
            
            <div className="form-group">
              <label>How much capital are you looking to raise?</label>
              <div className="currency-input-group">
                <select
                  className="currency-select"
                  value={formData.fundraise_currency}
                  onChange={(e) => updateField("fundraise_currency", e.target.value)}
                >
                  <option value="INR">₹ INR</option>
                  <option value="USD">$ USD</option>
                </select>
                <input
                  type="text"
                  className={`form-input currency-input ${errors.fundraise_amount ? 'error' : ''}`}
                  placeholder="e.g. 20000000"
                  value={formData.fundraise_amount}
                  onChange={(e) => updateField("fundraise_amount", e.target.value)}
                  data-testid="fundraise-amount-input"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Where are you in your fundraising journey?</label>
              <div className="fundraise-options">
                {fundraiseStages.map(stage => (
                  <button
                    key={stage}
                    type="button"
                    className={`fundraise-option ${formData.fundraise_stage === stage ? 'selected' : ''}`}
                    onClick={() => updateField("fundraise_stage", stage)}
                    data-testid={`fundraise-stage-${stage.substring(0, 10)}`}
                  >
                    {stage}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      
      case 12:
        return (
          <div className="form-step" data-testid="step-12">
            <h2 className="step-title">How did you hear about PW Family Office? <span className="required">*</span></h2>
            <p className="step-subtitle">We're curious — and it helps us know what's working!</p>
            
            <textarea
              className={`form-textarea ${errors.referral_source ? 'error' : ''}`}
              placeholder="LinkedIn? Twitter? A friend who wouldn't stop talking about us?"
              value={formData.referral_source}
              onChange={(e) => updateField("referral_source", e.target.value)}
              rows={3}
              data-testid="referral-textarea"
            />
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="application-form" data-testid="application-form">
      <div className="form-header">
        <button type="button" className="back-btn" onClick={handleBack} data-testid="back-btn">
          <span className="back-arrow">←</span>
        </button>
        <div className="progress-bar">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div 
              key={i} 
              className={`progress-segment ${i < step ? 'filled' : ''}`}
            />
          ))}
        </div>
        <span className="step-counter">{step}/{totalSteps}</span>
      </div>

      <div className="form-content">
        {renderStep()}
      </div>

      <div className="form-footer">
        {validationMessage && (
          <p className="validation-message" data-testid="validation-message">
            {validationMessage}
          </p>
        )}
        {step < totalSteps ? (
          <button 
            type="button"
            className="continue-btn" 
            onClick={handleContinue}
            data-testid="continue-btn"
          >
            CONTINUE <span className="arrow">→</span>
          </button>
        ) : (
          <button 
            type="button"
            className="submit-btn" 
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="submit-btn"
          >
            {submitting ? "SUBMITTING..." : "SUBMIT APPLICATION"}
          </button>
        )}
      </div>
    </div>
  );
};

// ============== SUCCESS PAGE ==============
const SuccessPage = () => {
  const navigate = useNavigate();

  return (
    <div className="success-page" data-testid="success-page">
      <div className="success-content">
        <div className="success-icon">
          <span className="checkmark">✓</span>
        </div>
        <h1 className="success-title">High five! 🙌</h1>
        <p className="success-subtitle">We've got your application.</p>
        <p className="success-subtitle">Expect to hear from us soon.</p>
        <button 
          className="back-home-btn" 
          onClick={() => navigate("/")}
          data-testid="back-home-btn"
        >
          BACK TO HOME
        </button>
      </div>
    </div>
  );
};

// ============== DASHBOARD LOGIN ==============
const DashboardLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      login(
        { id: response.data.id, name: response.data.name, email: response.data.email, role: response.data.role },
        response.data.token
      );
      navigate("/dashboard");
    } catch (err) {
      setError("Invalid credentials");
    }
    setLoading(false);
  };

  return (
    <div className="dashboard-login" data-testid="dashboard-login">
      <div className="login-card">
        <div className="login-icon">
          <span className="lock-icon">🔒</span>
        </div>
        <h1 className="login-title">PW Family Office</h1>
        <p className="login-subtitle">Enter your credentials to access the deal flow</p>
        
        <form onSubmit={handleLogin}>
          <input
            type="email"
            className="login-input"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
          />
          <input
            type="password"
            className="login-input"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
          />
          {error && <p className="login-error">{error}</p>}
          <button 
            type="submit" 
            className="login-btn"
            disabled={loading}
            data-testid="login-btn"
          >
            {loading ? "LOGGING IN..." : "ACCESS DASHBOARD"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============== DASHBOARD ==============
const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, user, logout } = useAuth();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({ 
    total: 0, new: 0, review: 0, short: 0, committed: 0, rejected: 0, track: 0,
    scout_breakdown: {}, assigned_breakdown: {}, source_breakdown: {}, 
    channel_breakdown: { inbound: {}, outbound: {} }, sector_breakdown: {},
    monthly_breakdown: {}
  });
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [sectorFilter, setSectorFilter] = useState(searchParams.get("sector") || "all");
  const [assignedFilter, setAssignedFilter] = useState(searchParams.get("assigned") || "all");
  const [scoutFilter, setScoutFilter] = useState(searchParams.get("scout") || "all");
  const [sourceFilter, setSourceFilter] = useState(searchParams.get("source") || "all");
  const [stageFilter, setStageFilter] = useState(searchParams.get("stage") || "all");
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "summary");
  const [showTeamStats, setShowTeamStats] = useState(() => {
    const saved = localStorage.getItem('showTeamStats');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  // Time period filter
  const [timePeriod, setTimePeriod] = useState(searchParams.get("period") || "all");
  const [customStartDate, setCustomStartDate] = useState(searchParams.get("start") || "");
  const [customEndDate, setCustomEndDate] = useState(searchParams.get("end") || "");
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(searchParams.get("period") === "custom");
  
  // Debounced versions of custom dates to prevent rapid API calls
  const [debouncedCustomStart, setDebouncedCustomStart] = useState(searchParams.get("start") || "");
  const [debouncedCustomEnd, setDebouncedCustomEnd] = useState(searchParams.get("end") || "");

  // Calculate date range based on time period
  const getDateRange = () => {
    const now = new Date();
    let startDate = null;
    let endDate = now.toISOString();
    
    if (timePeriod === "7days") {
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    } else if (timePeriod === "14days") {
      startDate = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString();
    } else if (timePeriod === "30days") {
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    } else if (timePeriod === "custom" && customStartDate && customEndDate) {
      // Format dates as ISO strings with time component
      startDate = customStartDate + "T00:00:00.000Z";
      endDate = customEndDate + "T23:59:59.999Z";
    } else {
      return { startDate: null, endDate: null };
    }
    
    return { startDate, endDate };
  };

  // Toggle team stats visibility (admin only)
  const toggleTeamStats = () => {
    const newValue = !showTeamStats;
    setShowTeamStats(newValue);
    localStorage.setItem('showTeamStats', JSON.stringify(newValue));
  };

  // Available sectors for filter
  const allSectors = ["AI", "Deep Tech", "B2C Consumer Tech", "SaaS & Enterprise", "D2C Consumer", "Manufacturing", "Fintech", "Healthcare", "Edtech", "Climate & Cleantech", "Foodtech", "Logistics", "E-Com/Q-Com", "Others"];

  // Format status for display
  const formatStatus = (status) => {
    const statusMap = {
      'new': 'NEW',
      'review': 'REVIEW',
      'short': 'SHORTLISTED',
      'committed': 'COMMITTED',
      'rejected': 'REJECTED',
      'track': 'TRACK'
    };
    return statusMap[status] || status.toUpperCase();
  };

  // Update URL params when filters or tab change (debounced to prevent circular updates)
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = new URLSearchParams();
      if (activeTab !== "summary") params.set("tab", activeTab);
      if (timePeriod !== "all") params.set("period", timePeriod);
      if (timePeriod === "custom" && customStartDate) params.set("start", customStartDate);
      if (timePeriod === "custom" && customEndDate) params.set("end", customEndDate);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (sectorFilter !== "all") params.set("sector", sectorFilter);
      if (assignedFilter !== "all") params.set("assigned", assignedFilter);
      if (scoutFilter !== "all") params.set("scout", scoutFilter);
      if (sourceFilter !== "all") params.set("source", sourceFilter);
      if (stageFilter !== "all") params.set("stage", stageFilter);
      if (debouncedSearch) params.set("search", debouncedSearch);
      setSearchParams(params, { replace: true });
    }, 100);
    return () => clearTimeout(timer);
  }, [activeTab, timePeriod, customStartDate, customEndDate, statusFilter, sectorFilter, assignedFilter, scoutFilter, sourceFilter, stageFilter, debouncedSearch]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Debounce custom dates together - only update when BOTH are valid
  useEffect(() => {
    if (customStartDate && customEndDate) {
      const timer = setTimeout(() => {
        setDebouncedCustomStart(customStartDate);
        setDebouncedCustomEnd(customEndDate);
      }, 400);
      return () => clearTimeout(timer);
    } else if (!customStartDate && !customEndDate) {
      // Clear debounced values when dates are cleared
      setDebouncedCustomStart("");
      setDebouncedCustomEnd("");
    }
  }, [customStartDate, customEndDate]);

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
      return;
    }
    
    // Skip fetch if custom period is selected but debounced dates are incomplete
    if (timePeriod === "custom" && (!debouncedCustomStart || !debouncedCustomEnd)) {
      return;
    }
    
    // AbortController to prevent race conditions with StrictMode double-mount
    const abortController = new AbortController();
    
    // Inline fetch to avoid stale closure issues
    const doFetch = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        // Calculate date range for STATS ONLY (not Deal Flow or Monthly Deals)
        // Time Period filter only affects: Deal Status, By Source, By Sectors, By Scout, By Assigned
        let statsStartDate = null;
        let statsEndDate = null;
        const now = new Date();
        
        if (timePeriod === "7days") {
          statsStartDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
          statsEndDate = now.toISOString();
        } else if (timePeriod === "14days") {
          statsStartDate = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString();
          statsEndDate = now.toISOString();
        } else if (timePeriod === "30days") {
          statsStartDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
          statsEndDate = now.toISOString();
        } else if (timePeriod === "custom" && debouncedCustomStart && debouncedCustomEnd) {
          statsStartDate = debouncedCustomStart + "T00:00:00.000Z";
          statsEndDate = debouncedCustomEnd + "T23:59:59.999Z";
        }
        // For "all" or incomplete custom, dates remain null (no date filter)
        
        // Applications params - NO time period filter (Deal Flow shows all deals)
        const appParams = { 
          status: statusFilter !== 'all' ? statusFilter : undefined, 
          sector: sectorFilter !== 'all' ? sectorFilter : undefined, 
          assigned: assignedFilter !== 'all' ? assignedFilter : undefined,
          scout: scoutFilter !== 'all' ? scoutFilter : undefined,
          source: sourceFilter !== 'all' ? sourceFilter : undefined,
          stage: stageFilter !== 'all' ? stageFilter : undefined,
          search: debouncedSearch || undefined
          // Note: start_date and end_date removed - Deal Flow is not filtered by time period
        };
        
        // Stats params - WITH time period filter (for Deal Status, By Source, etc.)
        // Note: Monthly Deals is excluded from this filter on the backend
        const statsParams = {
          start_date: statsStartDate || undefined,
          end_date: statsEndDate || undefined
        };
        
        const [appsRes, statsRes] = await Promise.all([
          axios.get(`${API}/applications`, { headers, params: appParams, signal: abortController.signal }),
          axios.get(`${API}/applications/stats`, { headers, params: statsParams, signal: abortController.signal })
        ]);
        
        // Only update state if not aborted
        if (!abortController.signal.aborted) {
          setApplications(appsRes.data);
          setStats(statsRes.data);
        }
      } catch (err) {
        // Ignore abort errors
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          return;
        }
        if (err.response?.status === 401) {
          logout();
          navigate("/dashboard/login");
        }
      }
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    };
    
    doFetch();
    fetchTeamMembers();
    
    // Cleanup: abort pending requests when dependencies change
    return () => {
      abortController.abort();
    };
  }, [token, statusFilter, sectorFilter, assignedFilter, scoutFilter, sourceFilter, stageFilter, debouncedSearch, timePeriod, debouncedCustomStart, debouncedCustomEnd]);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data);
    } catch (err) {
      console.error("Failed to fetch team members:", err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatAmount = (amount, currency) => {
    const num = parseFloat(amount);
    if (currency === "USD") {
      const millions = num / 1000000;
      return `$${millions.toFixed(1)} M`;
    } else {
      // INR - convert to Crores
      const crores = num / 10000000;
      return `₹${crores.toFixed(1)} Cr`;
    }
  };

  const formatDealId = (dealId) => {
    // Remove PW prefix and return just the number
    return dealId.replace('PW', '');
  };

  if (loading) {
    return <div className="dashboard-loading">Loading...</div>;
  }

  return (
    <div className="dashboard" data-testid="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">PW Family Office</h1>
        <div className="header-right">
          {user?.role === 'admin' && (
            <button 
              className="team-btn"
              onClick={() => navigate("/dashboard/team")}
              data-testid="team-btn"
            >
              👥 Team
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button 
          className={`dashboard-tab ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          Summary
        </button>
        <button 
          className={`dashboard-tab ${activeTab === 'dealflow' ? 'active' : ''}`}
          onClick={() => setActiveTab('dealflow')}
        >
          Deal Flow
        </button>
      </div>

      {/* Summary Tab Content */}
      {activeTab === 'summary' && (
        <div className="tab-content">
          {/* Time Period Filter - Only on Summary */}
          <div className="time-period-filter">
            <span className="filter-label">Time Period:</span>
            <select 
              value={timePeriod} 
              onChange={(e) => {
                setTimePeriod(e.target.value);
                if (e.target.value !== "custom") {
                  setShowCustomDatePicker(false);
                  setCustomStartDate("");
                  setCustomEndDate("");
                } else {
                  setShowCustomDatePicker(true);
                }
              }}
              className="time-period-select"
              data-testid="time-period-select"
            >
              <option value="all">All Time</option>
              <option value="7days">Last 7 Days</option>
              <option value="14days">Last 14 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
            
            {showCustomDatePicker && (
              <div className="custom-date-picker">
                <input 
                  type="date" 
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="date-input"
                  placeholder="Start Date"
                  data-testid="start-date"
                />
                <span className="date-separator">to</span>
                <input 
                  type="date" 
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="date-input"
                  placeholder="End Date"
                  data-testid="end-date"
                />
              </div>
            )}
          </div>

          {/* Status Summary */}
          <div className="summary-section">
            <h3 className="summary-section-title">Deal Status</h3>
            <div className="stats-grid">
              <div className={`stat-card ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => { setStatusFilter('all'); setActiveTab('dealflow'); }}>
                <span className="stat-number">{stats.total}</span>
                <span className="stat-label">TOTAL</span>
              </div>
              <div className={`stat-card new ${statusFilter === 'new' ? 'active' : ''}`} onClick={() => { setStatusFilter('new'); setActiveTab('dealflow'); }}>
                <span className="stat-number">{stats.new} <span className="percent">({stats.total ? Math.round((stats.new / stats.total) * 100) : 0}%)</span></span>
                <span className="stat-label">NEW</span>
              </div>
              <div className={`stat-card review ${statusFilter === 'review' ? 'active' : ''}`} onClick={() => { setStatusFilter('review'); setActiveTab('dealflow'); }}>
                <span className="stat-number">{stats.review} <span className="percent">({stats.total ? Math.round((stats.review / stats.total) * 100) : 0}%)</span></span>
                <span className="stat-label">REVIEW</span>
              </div>
              <div className={`stat-card rejected ${statusFilter === 'rejected' ? 'active' : ''}`} onClick={() => { setStatusFilter('rejected'); setActiveTab('dealflow'); }}>
                <span className="stat-number">{stats.rejected} <span className="percent">({stats.total ? Math.round((stats.rejected / stats.total) * 100) : 0}%)</span></span>
                <span className="stat-label">REJECTED</span>
              </div>
              <div className={`stat-card track ${statusFilter === 'track' ? 'active' : ''}`} onClick={() => { setStatusFilter('track'); setActiveTab('dealflow'); }}>
                <span className="stat-number">{stats.track} <span className="percent">({stats.total ? Math.round((stats.track / stats.total) * 100) : 0}%)</span></span>
                <span className="stat-label">TRACK</span>
              </div>
              <div className={`stat-card short ${statusFilter === 'short' ? 'active' : ''}`} onClick={() => { setStatusFilter('short'); setActiveTab('dealflow'); }}>
                <span className="stat-number">{stats.short} <span className="percent">({stats.total ? Math.round((stats.short / stats.total) * 100) : 0}%)</span></span>
                <span className="stat-label">SHORTLISTED</span>
              </div>
              <div className={`stat-card committed ${statusFilter === 'committed' ? 'active' : ''}`} onClick={() => { setStatusFilter('committed'); setActiveTab('dealflow'); }}>
                <span className="stat-number">{stats.committed} <span className="percent">({stats.total ? Math.round((stats.committed / stats.total) * 100) : 0}%)</span></span>
                <span className="stat-label">COMMITTED</span>
              </div>
            </div>
          </div>

          {/* Monthly Deals - Bar Chart */}
          <div className="summary-section">
            <h3 className="summary-section-title">Monthly Deals</h3>
            <div className="monthly-chart-container">
              {Object.keys(stats.monthly_breakdown || {}).length > 0 ? (
                <div className="bar-chart">
                  <div className="chart-bars">
                    {Object.entries(stats.monthly_breakdown || {})
                      .sort((a, b) => {
                        // Sort by date (oldest to newest)
                        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        const [monthA, yearA] = a[0].split(' ');
                        const [monthB, yearB] = b[0].split(' ');
                        if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
                        return months.indexOf(monthA) - months.indexOf(monthB);
                      })
                      .map(([month, count]) => {
                        const maxCount = Math.max(...Object.values(stats.monthly_breakdown || {}));
                        const heightPercent = maxCount > 0 ? (count / maxCount) * 100 : 0;
                        return (
                          <div key={month} className="bar-column">
                            <span className="bar-value">{count}</span>
                            <div className="bar" style={{ height: `${Math.max(heightPercent, 5)}%` }}></div>
                            <span className="bar-label">{month}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              ) : (
                <div className="no-chart-data">No data available</div>
              )}
            </div>
          </div>

          {/* Source Summary */}
          <div className="summary-section">
            <h3 className="summary-section-title">By Source</h3>
            <div className="source-breakdown-grid">
              {/* Inbound Card */}
              <div className="source-card">
                <div className="source-header">
                  <span className="source-name">Inbound</span>
                  <span className="source-total">{stats.source_breakdown?.['inbound'] || 0} <span className="percent">({stats.total ? Math.round(((stats.source_breakdown?.['inbound'] || 0) / stats.total) * 100) : 0}%)</span></span>
                </div>
                <div className="channel-list">
                  <div className="channel-item">
                    <span className="channel-name">VC Network</span>
                    <span className="channel-count">{stats.channel_breakdown?.inbound?.['vc_network'] || 0} <span className="percent">({stats.total ? Math.round(((stats.channel_breakdown?.inbound?.['vc_network'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Founder Network</span>
                    <span className="channel-count">{stats.channel_breakdown?.inbound?.['founder_network'] || 0} <span className="percent">({stats.total ? Math.round(((stats.channel_breakdown?.inbound?.['founder_network'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Prateek/Alakh</span>
                    <span className="channel-count">{stats.channel_breakdown?.inbound?.['prateek_alakh'] || 0} <span className="percent">({stats.total ? Math.round(((stats.channel_breakdown?.inbound?.['prateek_alakh'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Direct</span>
                    <span className="channel-count">{stats.channel_breakdown?.inbound?.['direct'] || 0} <span className="percent">({stats.total ? Math.round(((stats.channel_breakdown?.inbound?.['direct'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Others</span>
                    <span className="channel-count">{stats.channel_breakdown?.inbound?.['others'] || 0} <span className="percent">({stats.total ? Math.round(((stats.channel_breakdown?.inbound?.['others'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item muted">
                    <span className="channel-name">Not Set</span>
                    <span className="channel-count">{stats.channel_breakdown?.inbound?.['Not Set'] || 0} <span className="percent">({stats.total ? Math.round(((stats.channel_breakdown?.inbound?.['Not Set'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                </div>
              </div>

              {/* Outbound Card */}
              <div className="source-card">
                <div className="source-header">
                  <span className="source-name">Outbound</span>
                  <span className="source-total">{stats.source_breakdown?.['outbound'] || 0} <span className="percent">({stats.total ? Math.round(((stats.source_breakdown?.['outbound'] || 0) / stats.total) * 100) : 0}%)</span></span>
                </div>
                <div className="channel-list">
                  <div className="channel-item">
                    <span className="channel-name">LinkedIn</span>
                    <span className="channel-count">{stats.channel_breakdown?.outbound?.['linkedin'] || 0} <span className="percent">({stats.total ? Math.round(((stats.channel_breakdown?.outbound?.['linkedin'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Events</span>
                    <span className="channel-count">{stats.channel_breakdown?.outbound?.['events'] || 0} <span className="percent">({stats.total ? Math.round(((stats.channel_breakdown?.outbound?.['events'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Others</span>
                    <span className="channel-count">{stats.channel_breakdown?.outbound?.['others'] || 0} <span className="percent">({stats.total ? Math.round(((stats.channel_breakdown?.outbound?.['others'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item muted">
                    <span className="channel-name">Not Set</span>
                    <span className="channel-count">{stats.channel_breakdown?.outbound?.['Not Set'] || 0} <span className="percent">({stats.total ? Math.round(((stats.channel_breakdown?.outbound?.['Not Set'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                </div>
              </div>

              {/* Not Set Card */}
              <div className="source-card muted">
                <div className="source-header">
                  <span className="source-name">Not Set</span>
                  <span className="source-total">{stats.source_breakdown?.['Not Set'] || 0} <span className="percent">({stats.total ? Math.round(((stats.source_breakdown?.['Not Set'] || 0) / stats.total) * 100) : 0}%)</span></span>
                </div>
              </div>
            </div>
          </div>

          {/* Sectors Summary - Split into Type and Sector */}
          <div className="summary-section">
            <h3 className="summary-section-title">By Sectors</h3>
            <div className="source-breakdown-grid two-col">
              {/* Type Card */}
              <div className="source-card">
                <div className="source-header">
                  <span className="source-name">Type</span>
                  <span className="source-total"></span>
                </div>
                <div className="channel-list">
                  <div className="channel-item">
                    <span className="channel-name">AI</span>
                    <span className="channel-count">{stats.sector_breakdown?.['AI'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['AI'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Deep Tech</span>
                    <span className="channel-count">{stats.sector_breakdown?.['Deep Tech'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['Deep Tech'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">B2C Consumer Tech</span>
                    <span className="channel-count">{stats.sector_breakdown?.['B2C Consumer Tech'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['B2C Consumer Tech'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">SaaS & Enterprise</span>
                    <span className="channel-count">{stats.sector_breakdown?.['SaaS & Enterprise'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['SaaS & Enterprise'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">D2C Consumer</span>
                    <span className="channel-count">{stats.sector_breakdown?.['D2C Consumer'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['D2C Consumer'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Manufacturing</span>
                    <span className="channel-count">{stats.sector_breakdown?.['Manufacturing'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['Manufacturing'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                </div>
              </div>

              {/* Sector Card */}
              <div className="source-card">
                <div className="source-header">
                  <span className="source-name">Sector</span>
                  <span className="source-total"></span>
                </div>
                <div className="channel-list">
                  <div className="channel-item">
                    <span className="channel-name">Fintech</span>
                    <span className="channel-count">{stats.sector_breakdown?.['Fintech'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['Fintech'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Healthcare</span>
                    <span className="channel-count">{stats.sector_breakdown?.['Healthcare'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['Healthcare'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Edtech</span>
                    <span className="channel-count">{stats.sector_breakdown?.['Edtech'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['Edtech'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Climate & Cleantech</span>
                    <span className="channel-count">{stats.sector_breakdown?.['Climate & Cleantech'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['Climate & Cleantech'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Foodtech</span>
                    <span className="channel-count">{stats.sector_breakdown?.['Foodtech'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['Foodtech'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">Logistics</span>
                    <span className="channel-count">{stats.sector_breakdown?.['Logistics'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['Logistics'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                  <div className="channel-item">
                    <span className="channel-name">E-Com/Q-Com</span>
                    <span className="channel-count">{stats.sector_breakdown?.['E-Com/Q-Com'] || 0} <span className="percent">({stats.total ? Math.round(((stats.sector_breakdown?.['E-Com/Q-Com'] || 0) / stats.total) * 100) : 0}%)</span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Toggle for Team Stats */}
          {user?.role === 'admin' && (
            <div className="team-stats-toggle">
              <label className="toggle-label">
                <input 
                  type="checkbox" 
                  checked={showTeamStats} 
                  onChange={toggleTeamStats}
                  className="toggle-checkbox"
                />
                <span className="toggle-text">Show Team Stats (Scout & Assigned)</span>
              </label>
            </div>
          )}

          {/* Scout Summary - Admin Only */}
          {user?.role === 'admin' && showTeamStats && (
          <div className="summary-section">
            <h3 className="summary-section-title">By Scout</h3>
            <div className="breakdown-grid-5">
              <div className="breakdown-card">
                <span className="breakdown-name">Varun</span>
                <span className="breakdown-count">{stats.scout_breakdown?.['Varun'] || 0} <span className="percent">({stats.total ? Math.round(((stats.scout_breakdown?.['Varun'] || 0) / stats.total) * 100) : 0}%)</span></span>
              </div>
              <div className="breakdown-card">
                <span className="breakdown-name">Angel</span>
                <span className="breakdown-count">{stats.scout_breakdown?.['Angel'] || 0} <span className="percent">({stats.total ? Math.round(((stats.scout_breakdown?.['Angel'] || 0) / stats.total) * 100) : 0}%)</span></span>
              </div>
              <div className="breakdown-card">
                <span className="breakdown-name">Muskan</span>
                <span className="breakdown-count">{stats.scout_breakdown?.['Muskan'] || 0} <span className="percent">({stats.total ? Math.round(((stats.scout_breakdown?.['Muskan'] || 0) / stats.total) * 100) : 0}%)</span></span>
              </div>
              <div className="breakdown-card">
                <span className="breakdown-name">Direct</span>
                <span className="breakdown-count">{stats.scout_breakdown?.['Direct'] || 0} <span className="percent">({stats.total ? Math.round(((stats.scout_breakdown?.['Direct'] || 0) / stats.total) * 100) : 0}%)</span></span>
              </div>
              <div className="breakdown-card muted">
                <span className="breakdown-name">Not Set</span>
                <span className="breakdown-count">{stats.scout_breakdown?.['Unassigned'] || 0} <span className="percent">({stats.total ? Math.round(((stats.scout_breakdown?.['Unassigned'] || 0) / stats.total) * 100) : 0}%)</span></span>
              </div>
            </div>
          </div>
          )}

          {/* Assigned To Summary - Admin Only */}
          {user?.role === 'admin' && showTeamStats && (
          <div className="summary-section">
            <h3 className="summary-section-title">By Assigned</h3>
            <div className="breakdown-grid-4">
              <div className="breakdown-card">
                <span className="breakdown-name">Varun</span>
                <span className="breakdown-count">{stats.assigned_breakdown?.['Varun'] || 0} <span className="percent">({stats.total ? Math.round(((stats.assigned_breakdown?.['Varun'] || 0) / stats.total) * 100) : 0}%)</span></span>
              </div>
              <div className="breakdown-card">
                <span className="breakdown-name">Angel</span>
                <span className="breakdown-count">{stats.assigned_breakdown?.['Angel'] || 0} <span className="percent">({stats.total ? Math.round(((stats.assigned_breakdown?.['Angel'] || 0) / stats.total) * 100) : 0}%)</span></span>
              </div>
              <div className="breakdown-card">
                <span className="breakdown-name">Muskan</span>
                <span className="breakdown-count">{stats.assigned_breakdown?.['Muskan'] || 0} <span className="percent">({stats.total ? Math.round(((stats.assigned_breakdown?.['Muskan'] || 0) / stats.total) * 100) : 0}%)</span></span>
              </div>
              <div className="breakdown-card muted">
                <span className="breakdown-name">Not Set</span>
                <span className="breakdown-count">{stats.assigned_breakdown?.['Unassigned'] || 0} <span className="percent">({stats.total ? Math.round(((stats.assigned_breakdown?.['Unassigned'] || 0) / stats.total) * 100) : 0}%)</span></span>
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Deal Flow Tab Content */}
      {activeTab === 'dealflow' && (
        <div className="tab-content">
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by company or founder..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
            />
            {search && (
              <button 
                type="button"
                className="search-clear-btn"
                onClick={() => setSearch("")}
                data-testid="search-clear-btn"
              >
                ✕
              </button>
            )}
          </div>

          <div className="filter-bar">
            <span className="filter-label">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-select"
              data-testid="status-filter"
            >
              <option value="all">All</option>
              <option value="new">New</option>
              <option value="review">Review</option>
              <option value="short">Shortlisted</option>
              <option value="track">Track</option>
              <option value="committed">Committed</option>
              <option value="rejected">Rejected</option>
            </select>

            <span className="filter-label">Stage:</span>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="status-select"
              data-testid="stage-filter"
            >
              <option value="all">All</option>
              <option value="Pre-Seed">Pre-Seed</option>
              <option value="Seed">Seed</option>
              <option value="Pre-Series A">Pre-Series A</option>
              <option value="Series A">Series A</option>
              <option value="Pre-Series B">Pre-Series B</option>
              <option value="Series B">Series B</option>
            </select>

            <span className="filter-label">Sector:</span>
            <select
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e.target.value)}
              className="status-select"
              data-testid="sector-filter"
            >
              <option value="all">All Sectors</option>
              {allSectors.map(sector => (
                <option key={sector} value={sector}>{sector}</option>
              ))}
            </select>

            <span className="filter-label">Assigned:</span>
            <select
              value={assignedFilter}
              onChange={(e) => setAssignedFilter(e.target.value)}
              className="status-select"
              data-testid="assigned-filter"
            >
              <option value="all">All</option>
              <option value="unassigned">Unassigned</option>
              {teamMembers.map(member => (
                <option key={member.id} value={member.id}>
                  {member.email === 'varni.agarwal@gmail.com' ? 'Varun' : member.name}
                </option>
              ))}
            </select>

            <span className="filter-label">Scout:</span>
            <select
              value={scoutFilter}
              onChange={(e) => setScoutFilter(e.target.value)}
              className="status-select"
              data-testid="scout-filter"
            >
              <option value="all">All</option>
              <option value="unassigned">Not Set</option>
              <option value="direct">Direct</option>
              {teamMembers.map(member => (
                <option key={member.id} value={member.id}>
                  {member.email === 'varni.agarwal@gmail.com' ? 'Varun' : member.name}
                </option>
              ))}
            </select>

            <span className="filter-label">Source:</span>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="status-select"
              data-testid="source-filter"
            >
              <option value="all">All</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
              <option value="not_set">Not Set</option>
            </select>

            {/* Clear Filters Button - only show if any filter is active */}
            {(statusFilter !== 'all' || sectorFilter !== 'all' || assignedFilter !== 'all' || scoutFilter !== 'all' || sourceFilter !== 'all' || stageFilter !== 'all' || search) && (
              <button
                className="clear-filters-btn"
                onClick={() => {
                  setStatusFilter('all');
                  setSectorFilter('all');
                  setAssignedFilter('all');
                  setScoutFilter('all');
                  setSourceFilter('all');
                  setStageFilter('all');
                  setSearch('');
                }}
                data-testid="clear-filters-btn"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Total entries text */}
          <div className="entries-count">
            Showing {applications.length} {applications.length === 1 ? 'entry' : 'entries'}
          </div>

          {/* Table Header */}
          <div className="deals-table">
            <div className="deals-table-header">
              <div className="col-id">ID</div>
              <div className="col-startup">STARTUP</div>
              <div className="col-founder">FOUNDER</div>
              <div className="col-sectors">SECTORS</div>
              <div className="col-ask">ASK</div>
              <div className="col-stage">STAGE</div>
              <div className="col-date">DATE</div>
              <div className="col-status">STATUS</div>
            </div>
            
            <div className="deals-table-body">
              {applications.map(app => (
                <div 
                  key={app.id} 
                  className="deals-table-row"
                  onClick={() => {
                    // Preserve current filters in the URL when navigating to deal detail
                    const currentParams = new URLSearchParams(searchParams);
                    currentParams.set('tab', 'dealflow');
                    navigate(`/dashboard/deal/${app.id}?returnParams=${encodeURIComponent(currentParams.toString())}`);
                  }}
                  data-testid={`deal-${app.deal_id}`}
                >
                  {/* Desktop view columns */}
                  <div className="col-id desktop-only">{formatDealId(app.deal_id)}</div>
                  <div className="col-startup desktop-only">{app.startup_name}</div>
                  <div className="col-founder desktop-only">{app.first_name} {app.last_name}</div>
                  <div className="col-sectors desktop-only">{app.sectors.slice(0, 2).join(", ")}</div>
                  <div className="col-ask desktop-only">{formatAmount(app.fundraise_amount, app.fundraise_currency)}</div>
                  <div className="col-stage desktop-only">{app.stage || "-"}</div>
                  <div className="col-date desktop-only">{formatDate(app.created_at)}</div>
                  <div className="col-status desktop-only">
                    <span className={`status-badge ${app.status}`}>{formatStatus(app.status)}</span>
                  </div>
                  
                  {/* Mobile card view */}
                  <div className="mobile-card">
                    <div className="mobile-card-header">
                      <div className="mobile-card-title-section">
                        <h3 className="mobile-card-startup">{app.startup_name}</h3>
                        <span className="mobile-card-id">{formatDealId(app.deal_id)}</span>
                      </div>
                      <span className={`status-badge ${app.status}`}>{formatStatus(app.status)}</span>
                    </div>
                    
                    <div className="mobile-card-founder">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      {app.first_name} {app.last_name}
                    </div>
                    
                    <div className="mobile-card-sectors">
                      {app.sectors.slice(0, 3).map((sector, idx) => (
                        <span key={idx} className="mobile-sector-tag">{sector}</span>
                      ))}
                    </div>
                    
                    <div className="mobile-card-footer">
                      <div className="mobile-card-amount">
                        {formatAmount(app.fundraise_amount, app.fundraise_currency)}
                      </div>
                      <div className="mobile-card-date">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                          <line x1="16" y1="2" x2="16" y2="6"/>
                          <line x1="8" y1="2" x2="8" y2="6"/>
                          <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                        {formatDate(app.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {applications.length === 0 && (
              <div className="no-deals">No applications found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============== DEAL DETAIL ==============
const DealDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user, logout } = useAuth();
  const [application, setApplication] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [addingComment, setAddingComment] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [newResourceType, setNewResourceType] = useState("link");
  const [newResourceName, setNewResourceName] = useState("");
  const [newResourceUrl, setNewResourceUrl] = useState("");
  const [addingResource, setAddingResource] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [localReferralSource, setLocalReferralSource] = useState("");
  
  // Get the return params from URL to preserve filters when going back
  const returnParams = searchParams.get('returnParams') || 'tab=dealflow';
  
  const handleBackToDashboard = () => {
    navigate(`/dashboard?${returnParams}`);
  };

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
      return;
    }
    fetchApplication();
    fetchComments();
    fetchTeamMembers();
  }, [id, token]);

  const fetchApplication = async () => {
    try {
      const response = await axios.get(`${API}/applications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplication(response.data);
      setEditData(response.data);
      setLocalReferralSource(response.data.referral_source || "");
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        navigate("/dashboard/login");
      }
    }
    setLoading(false);
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/applications/${id}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data);
    } catch (err) {
      console.error("Failed to fetch comments:", err);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data);
    } catch (err) {
      console.error("Failed to fetch team members:", err);
    }
  };

  const deleteApplication = async () => {
    if (!window.confirm("Are you sure you want to delete this application? This action cannot be undone.")) {
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/applications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate("/dashboard");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete application");
    }
    setDeleting(false);
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    
    setAddingComment(true);
    try {
      await axios.post(`${API}/applications/${id}/comments`, 
        { text: newComment },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setNewComment("");
      fetchComments();
    } catch (err) {
      console.error("Failed to add comment:", err);
    }
    setAddingComment(false);
  };

  const updateComment = async (commentId) => {
    if (!editingCommentText.trim()) return;
    
    try {
      await axios.patch(`${API}/applications/${id}/comments/${commentId}`, 
        { text: editingCommentText },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setEditingCommentId(null);
      setEditingCommentText("");
      fetchComments();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update comment");
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    
    try {
      await axios.delete(`${API}/applications/${id}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchComments();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete comment");
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      await axios.patch(`${API}/applications/${id}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setApplication(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const assignApplication = async (memberId, memberName) => {
    try {
      await axios.patch(`${API}/applications/${id}/assign`, 
        { assigned_to: memberId, assigned_to_name: memberName },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setApplication(prev => ({ ...prev, assigned_to: memberId, assigned_to_name: memberName }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to assign application");
    }
  };

  const setScout = async (scoutId, scoutName) => {
    try {
      await axios.patch(`${API}/applications/${id}/scout`, 
        { scout_id: scoutId, scout_name: scoutName },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setApplication(prev => ({ ...prev, scout_id: scoutId, scout_name: scoutName }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to set scout");
    }
  };

  const setSourceChannel = async (source, channel) => {
    try {
      await axios.patch(`${API}/applications/${id}/source-channel`, 
        { source, channel },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setApplication(prev => ({ ...prev, source, channel }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to set source/channel");
    }
  };

  const updateReferralSource = async (referral_source) => {
    try {
      await axios.patch(`${API}/applications/${id}`, 
        { referral_source },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setApplication(prev => ({ ...prev, referral_source }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update referral source");
    }
  };

  const updateStage = async (stage) => {
    try {
      await axios.patch(`${API}/applications/${id}`, 
        { stage },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setApplication(prev => ({ ...prev, stage }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update stage");
    }
  };

  const stageOptions = [
    "Pre-Seed",
    "Seed",
    "Pre-Series A",
    "Series A",
    "Pre-Series B",
    "Series B"
  ];

  const addInternalResource = async () => {
    if (!newResourceName.trim() || !newResourceUrl.trim()) {
      alert("Please enter both name and URL");
      return;
    }
    setAddingResource(true);
    try {
      const response = await axios.post(`${API}/applications/${id}/internal-resources`, 
        { type: newResourceType, name: newResourceName, url: newResourceUrl },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setApplication(prev => ({
        ...prev,
        internal_resources: [...(prev.internal_resources || []), response.data.resource]
      }));
      setNewResourceName("");
      setNewResourceUrl("");
      setNewResourceType("link");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to add resource");
    }
    setAddingResource(false);
  };

  const uploadInternalFile = async () => {
    if (!selectedFile) {
      alert("Please select a file");
      return;
    }
    setAddingResource(true);
    setUploadProgress(0);
    
    const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
    const totalChunks = Math.ceil(selectedFile.size / CHUNK_SIZE);
    const resourceName = newResourceName.trim() || selectedFile.name;
    
    try {
      // For small files (< 5MB), use regular upload
      if (selectedFile.size < 5 * 1024 * 1024) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('resource_name', resourceName);
        
        const response = await axios.post(`${API}/applications/${id}/internal-resources/upload`, 
          formData,
          { 
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percent);
            }
          }
        );
        setApplication(prev => ({
          ...prev,
          internal_resources: [...(prev.internal_resources || []), response.data.resource]
        }));
      } else {
        // For large files, use chunked upload
        // Step 1: Initialize upload
        const initResponse = await axios.post(
          `${API}/applications/${id}/chunked-upload/init`,
          {
            filename: selectedFile.name,
            total_size: selectedFile.size,
            total_chunks: totalChunks,
            resource_name: resourceName
          },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        
        const uploadId = initResponse.data.upload_id;
        
        // Step 2: Upload chunks
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, selectedFile.size);
          const chunk = selectedFile.slice(start, end);
          
          const chunkFormData = new FormData();
          chunkFormData.append('file', chunk);
          
          await axios.post(
            `${API}/applications/${id}/chunked-upload/${uploadId}/chunk/${i}`,
            chunkFormData,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }}
          );
          
          // Update progress
          const percent = Math.round(((i + 1) / totalChunks) * 100);
          setUploadProgress(percent);
        }
        
        // Step 3: Complete upload
        const completeResponse = await axios.post(
          `${API}/applications/${id}/chunked-upload/${uploadId}/complete`,
          {},
          { headers: { Authorization: `Bearer ${token}` }}
        );
        
        setApplication(prev => ({
          ...prev,
          internal_resources: [...(prev.internal_resources || []), completeResponse.data.resource]
        }));
      }
      
      setSelectedFile(null);
      setNewResourceName("");
      setNewResourceType("link");
      setUploadProgress(0);
      // Reset file input
      const fileInput = document.getElementById('resource-file-input');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to upload file");
      setUploadProgress(0);
    }
    setAddingResource(false);
  };

  const deleteInternalResource = async (resourceId) => {
    if (!window.confirm("Delete this resource?")) return;
    try {
      await axios.delete(`${API}/applications/${id}/internal-resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplication(prev => ({
        ...prev,
        internal_resources: (prev.internal_resources || []).filter(r => r.id !== resourceId)
      }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete resource");
    }
  };

  const saveEdits = async () => {
    try {
      await axios.patch(`${API}/applications/${id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplication(editData);
      setIsEditing(false);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save changes");
    }
  };

  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const canAssign = user?.role === 'admin' || user?.role === 'editor';
  const canDelete = user?.role === 'admin';
  const canChangeStatus = user?.role === 'admin' || user?.role === 'editor';
  const canComment = user?.role === 'admin' || user?.role === 'editor';

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatAmount = (amount, currency) => {
    const num = parseFloat(amount);
    if (currency === "USD") {
      const millions = num / 1000000;
      return `$${millions.toFixed(1)} M`;
    } else {
      // INR - convert to Crores
      const crores = num / 10000000;
      return `₹${crores.toFixed(1)} Cr`;
    }
  };

  const formatDealId = (dealId) => {
    return dealId.replace('PW', '');
  };

  if (loading) return <div className="dashboard-loading">Loading...</div>;
  if (!application) return <div className="dashboard-loading">Application not found</div>;

  return (
    <div className="deal-detail" data-testid="deal-detail">
      <div className="detail-header">
        <button className="back-btn" onClick={handleBackToDashboard} data-testid="back-to-deals">
          ← Back to deals
        </button>
        {canEdit && !isEditing && (
          <button className="edit-btn" onClick={() => setIsEditing(true)} data-testid="edit-btn">
            ✏️ Edit
          </button>
        )}
        {isEditing && (
          <div className="edit-actions">
            <button className="save-btn" onClick={saveEdits} data-testid="save-btn">Save</button>
            <button className="cancel-btn" onClick={() => { setIsEditing(false); setEditData(application); }} data-testid="cancel-btn">Cancel</button>
          </div>
        )}
      </div>

      <div className="detail-content">
        <div className="detail-top">
          <div>
            <div className="deal-id-date-row">
              <span className="deal-id-large">{formatDealId(application.deal_id)}</span>
              <span className="application-date-top">{formatDate(application.created_at)}</span>
            </div>
            {isEditing ? (
              <input 
                type="text" 
                className="edit-input title-input"
                value={editData.startup_name}
                onChange={(e) => setEditData({...editData, startup_name: e.target.value})}
              />
            ) : (
              <h1 className="detail-title">{application.startup_name}</h1>
            )}
          </div>
          <div className="status-actions-container">
            <div className="status-actions">
              <div className="filter-inline">
                <label>Status:</label>
                <select
                  value={application.status}
                  onChange={(e) => updateStatus(e.target.value)}
                  className="filter-select"
                  data-testid="status-select"
                  disabled={!canChangeStatus}
                >
                  <option value="new">New</option>
                  <option value="review">Review</option>
                  <option value="short">Shortlisted</option>
                  <option value="track">Track</option>
                  <option value="committed">Committed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              
              <div className="filter-inline">
                <label>Assigned:</label>
                {canAssign ? (
                  <select
                    value={application.assigned_to || ""}
                    onChange={(e) => {
                      const member = teamMembers.find(m => m.id === e.target.value);
                      const displayName = member?.email === 'varni.agarwal@gmail.com' ? 'Varun' : member?.name;
                      assignApplication(e.target.value || null, displayName || null);
                    }}
                    className="filter-select"
                    data-testid="assignment-select"
                  >
                    <option value="">Unassigned</option>
                    {teamMembers.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.email === 'varni.agarwal@gmail.com' ? 'Varun' : member.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="filter-value">{application.assigned_to_name || "Unassigned"}</span>
                )}
              </div>

              <div className="filter-inline">
                <label>Scout:</label>
                {canAssign ? (
                  <select
                    value={application.scout_id || ""}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      if (selectedValue === 'direct') {
                        setScout('direct', 'Direct');
                      } else {
                        const member = teamMembers.find(m => m.id === selectedValue);
                        const displayName = member?.email === 'varni.agarwal@gmail.com' ? 'Varun' : member?.name;
                        setScout(selectedValue || null, displayName || null);
                      }
                    }}
                    className="filter-select"
                    data-testid="scout-select"
                  >
                    <option value="">No Scout</option>
                    <option value="direct">Direct</option>
                    {teamMembers.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.email === 'varni.agarwal@gmail.com' ? 'Varun' : member.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="filter-value">{application.scout_name || "No Scout"}</span>
                )}
              </div>

              <div className="filter-inline">
                <label>Stage:</label>
                {canChangeStatus ? (
                  <select
                    value={application.stage || ""}
                    onChange={(e) => updateStage(e.target.value)}
                    className="filter-select"
                    data-testid="stage-select"
                  >
                    <option value="">Select</option>
                    {stageOptions.map(stage => (
                      <option key={stage} value={stage}>{stage}</option>
                    ))}
                  </select>
                ) : (
                  <span className="filter-value">{application.stage || "Not Set"}</span>
                )}
              </div>
            </div>

            <div className="status-actions">
              <div className="filter-inline">
                <label>Source:</label>
                {canAssign ? (
                  <select
                    value={application.source || ""}
                    onChange={(e) => {
                      const newSource = e.target.value || null;
                      setSourceChannel(newSource, null);
                    }}
                    className="filter-select"
                    data-testid="source-select"
                  >
                    <option value="">Select</option>
                    <option value="inbound">Inbound</option>
                    <option value="outbound">Outbound</option>
                  </select>
                ) : (
                  <span className="filter-value">{application.source ? (application.source.charAt(0).toUpperCase() + application.source.slice(1)) : "Not Set"}</span>
                )}
              </div>

              <div className="filter-inline">
                <label>Channel:</label>
                {canAssign ? (
                  <select
                    value={application.channel || ""}
                    onChange={(e) => {
                      setSourceChannel(application.source, e.target.value || null);
                    }}
                    className="filter-select"
                    data-testid="channel-select"
                    disabled={!application.source}
                  >
                    <option value="">Select</option>
                    {application.source === 'inbound' && (
                      <>
                        <option value="vc_network">VC Network</option>
                        <option value="founder_network">Founder Network</option>
                        <option value="prateek_alakh">Prateek/Alakh</option>
                        <option value="direct">Direct</option>
                        <option value="others">Others</option>
                      </>
                    )}
                    {application.source === 'outbound' && (
                      <>
                        <option value="linkedin">Linkedin</option>
                        <option value="events">Events</option>
                        <option value="others">Others</option>
                      </>
                    )}
                  </select>
                ) : (
                  <span className="filter-value">
                    {application.channel ? application.channel.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Not Set"}
                  </span>
                )}
              </div>

              <div className="filter-inline">
                <label>Referral:</label>
                {canAssign ? (
                  <input
                    type="text"
                    value={localReferralSource}
                    onChange={(e) => setLocalReferralSource(e.target.value)}
                    onBlur={(e) => {
                      if (e.target.value !== application.referral_source) {
                        updateReferralSource(e.target.value);
                      }
                    }}
                    className="filter-input"
                    placeholder="Specific Name"
                    data-testid="referral-input"
                  />
                ) : (
                  <span className="filter-value">{application.referral_source || "Not Set"}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="detail-sections">
          {/* Section 1: Contact Information */}
          <div className="detail-section">
            <h3>Contact Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Name</span>
                {isEditing ? (
                  <div className="edit-row">
                    <input type="text" className="edit-input" value={editData.first_name} onChange={(e) => setEditData({...editData, first_name: e.target.value})} placeholder="First" />
                    <input type="text" className="edit-input" value={editData.last_name} onChange={(e) => setEditData({...editData, last_name: e.target.value})} placeholder="Last" />
                  </div>
                ) : (
                  <span className="info-value">{application.first_name} {application.last_name}</span>
                )}
              </div>
              <div className="info-item">
                <span className="info-label">Phone</span>
                {isEditing ? (
                  <input type="text" className="edit-input" value={editData.phone_number} onChange={(e) => setEditData({...editData, phone_number: e.target.value})} />
                ) : (
                  <span className="info-value">{application.phone_code} {application.phone_number}</span>
                )}
              </div>
              <div className="info-item">
                <span className="info-label">Email</span>
                {isEditing ? (
                  <input type="email" className="edit-input" value={editData.email} onChange={(e) => setEditData({...editData, email: e.target.value})} />
                ) : (
                  <span className="info-value">{application.email}</span>
                )}
              </div>
            </div>
          </div>

          {/* Section 2: Elevator Pitch */}
          <div className="detail-section">
            <h3>Elevator Pitch</h3>
            {isEditing ? (
              <textarea 
                className="edit-textarea"
                value={editData.elevator_pitch}
                onChange={(e) => setEditData({...editData, elevator_pitch: e.target.value})}
                rows={4}
              />
            ) : (
              <p className="pitch-text">{application.elevator_pitch}</p>
            )}
          </div>

          {/* Section 3: Startup Details */}
          <div className="detail-section">
            <h3>Startup Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Location</span>
                {isEditing ? (
                  <input type="text" className="edit-input" value={editData.city} onChange={(e) => setEditData({...editData, city: e.target.value})} />
                ) : (
                  <span className="info-value">{application.city}</span>
                )}
              </div>
              <div className="info-item">
                <span className="info-label">Started</span>
                {isEditing ? (
                  <div className="edit-row">
                    <input type="text" className="edit-input" value={editData.start_month} onChange={(e) => setEditData({...editData, start_month: e.target.value})} placeholder="Month" />
                    <input type="text" className="edit-input" value={editData.start_year} onChange={(e) => setEditData({...editData, start_year: e.target.value})} placeholder="Year" />
                  </div>
                ) : (
                  <span className="info-value">{application.start_month} {application.start_year}</span>
                )}
              </div>
              <div className="info-item">
                <span className="info-label">Total Founders</span>
                {isEditing ? (
                  <input type="text" className="edit-input" value={editData.num_founders} onChange={(e) => setEditData({...editData, num_founders: e.target.value})} />
                ) : (
                  <span className="info-value">{application.num_founders}</span>
                )}
              </div>
              <div className="info-item">
                <span className="info-label">Stage</span>
                {isEditing ? (
                  <input type="text" className="edit-input" value={editData.startup_stage} onChange={(e) => setEditData({...editData, startup_stage: e.target.value})} />
                ) : (
                  <span className="info-value">{application.startup_stage}</span>
                )}
              </div>
            </div>
            
            {/* Sectors - separate from info-grid for proper editing layout */}
            <div className="sectors-section">
              <span className="info-label">Sectors</span>
              {isEditing ? (
                <div className="sectors-pills-container">
                  {["AI", "Deep Tech", "B2C Consumer Tech", "SaaS & Enterprise", "D2C Consumer", "Manufacturing", "Fintech", "Healthcare", "Edtech", "Climate & Cleantech", "Foodtech", "Logistics", "E-Com/Q-Com", "Others"].map(sector => (
                    <button
                      key={sector}
                      type="button"
                      onClick={() => {
                        const currentSectors = editData.sectors || [];
                        const newSectors = currentSectors.includes(sector)
                          ? currentSectors.filter(s => s !== sector)
                          : [...currentSectors, sector];
                        setEditData({...editData, sectors: newSectors});
                      }}
                      className={`sector-pill-btn ${(editData.sectors || []).includes(sector) ? 'selected' : ''}`}
                    >
                      {sector}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="sectors-pills-display">
                  {application.sectors.map((sector, idx) => (
                    <span key={idx} className="sector-pill-view">{sector}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Section 3: Founder Profiles */}
          <div className="detail-section">
            <h3>Founder Profiles {isEditing && <span className="edit-hint">(Edit names & LinkedIn URLs)</span>}</h3>
            {isEditing ? (
              <div className="founders-edit-grid">
                {/* Founder 1 - Main Applicant */}
                <div className="founder-edit-card">
                  <span className="founder-number">Founder 1 (Main)</span>
                  <input 
                    type="text" 
                    className="edit-input"
                    value={`${editData.first_name} ${editData.last_name}`}
                    disabled
                    placeholder="Name (edit in Contact Info)"
                  />
                  <input 
                    type="text" 
                    className="edit-input"
                    value={editData.linkedin_url || ''}
                    onChange={(e) => setEditData({...editData, linkedin_url: e.target.value})}
                    placeholder="LinkedIn URL"
                  />
                </div>
                
                {/* Co-founders */}
                {(editData.cofounder_names || []).map((name, idx) => (
                  <div key={idx} className="founder-edit-card">
                    <div className="founder-edit-header">
                      <span className="founder-number">Co-Founder {idx + 1}</span>
                      <button 
                        type="button"
                        className="remove-founder-btn"
                        onClick={() => {
                          const newNames = [...(editData.cofounder_names || [])];
                          const newLinkedins = [...(editData.cofounder_linkedins || [])];
                          newNames.splice(idx, 1);
                          newLinkedins.splice(idx, 1);
                          setEditData({...editData, cofounder_names: newNames, cofounder_linkedins: newLinkedins});
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    <input 
                      type="text" 
                      className="edit-input"
                      value={name || ''}
                      onChange={(e) => {
                        const newNames = [...(editData.cofounder_names || [])];
                        newNames[idx] = e.target.value;
                        setEditData({...editData, cofounder_names: newNames});
                      }}
                      placeholder="Co-founder name"
                    />
                    <input 
                      type="text" 
                      className="edit-input"
                      value={(editData.cofounder_linkedins || [])[idx] || ''}
                      onChange={(e) => {
                        const newLinkedins = [...(editData.cofounder_linkedins || [])];
                        newLinkedins[idx] = e.target.value;
                        setEditData({...editData, cofounder_linkedins: newLinkedins});
                      }}
                      placeholder="LinkedIn URL"
                    />
                  </div>
                ))}
                
                {/* Add Co-founder Button */}
                <button 
                  type="button"
                  className="add-founder-btn"
                  onClick={() => {
                    const newNames = [...(editData.cofounder_names || []), ''];
                    const newLinkedins = [...(editData.cofounder_linkedins || []), ''];
                    setEditData({...editData, cofounder_names: newNames, cofounder_linkedins: newLinkedins});
                  }}
                >
                  + Add Co-Founder
                </button>
              </div>
            ) : (
              <div className="founder-profiles-grid">
                {/* Founder 1 - Main Applicant */}
                <a 
                  href={application.linkedin_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="founder-profile-card"
                  data-testid="founder-1-linkedin"
                >
                  <span className="founder-number">Founder 1</span>
                  <span className="founder-name-link">{application.first_name} {application.last_name}</span>
                  <span className="view-profile-text">View LinkedIn →</span>
                </a>
                
                {/* Co-founders */}
                {application.cofounder_linkedins && application.cofounder_linkedins.filter(l => l && l.trim()).map((link, idx) => (
                  <a 
                    key={idx}
                    href={link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="founder-profile-card"
                    data-testid={`founder-${idx + 2}-linkedin`}
                  >
                    <span className="founder-number">Founder {idx + 2}</span>
                    <span className="founder-name-link">
                      {application.cofounder_names && application.cofounder_names[idx] 
                        ? application.cofounder_names[idx] 
                        : "Co-founder"}
                    </span>
                    <span className="view-profile-text">View LinkedIn →</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Section 4: Fundraising */}
          <div className="detail-section">
            <h3>Fundraising</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Amount</span>
                {isEditing ? (
                  <input type="text" className="edit-input" value={editData.fundraise_amount} onChange={(e) => setEditData({...editData, fundraise_amount: e.target.value})} />
                ) : (
                  <span className="info-value">{formatAmount(application.fundraise_amount, application.fundraise_currency)}</span>
                )}
              </div>
              <div className="info-item">
                <span className="info-label">Stage</span>
                {isEditing ? (
                  <input type="text" className="edit-input" value={editData.fundraise_stage} onChange={(e) => setEditData({...editData, fundraise_stage: e.target.value})} />
                ) : (
                  <span className="info-value">{application.fundraise_stage}</span>
                )}
              </div>
            </div>
          </div>

          {/* Section 5: Supporting Materials (combined - files, links, pitch deck) */}
          <div className="detail-section">
            <h3>Supporting Materials {isEditing && <span className="edit-hint">(Edit files & links)</span>}</h3>
            
            {isEditing ? (
              <div className="materials-edit">
                {/* Pitch Deck Section */}
                <h4 className="materials-subtitle">Pitch Deck</h4>
                {editData.pitch_deck_url ? (
                  <div className="file-edit-row">
                    <span className="file-name">📊 {editData.pitch_deck_name}</span>
                    <button 
                      type="button"
                      className="remove-link-btn"
                      onClick={() => setEditData({...editData, pitch_deck_url: '', pitch_deck_name: ''})}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <label className="add-file-btn pitch-deck-upload">
                    <input 
                      type="file" 
                      accept=".pdf,.ppt,.pptx"
                      style={{display: 'none'}}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const response = await axios.post(`${API}/upload`, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          setEditData({
                            ...editData, 
                            pitch_deck_url: response.data.url,
                            pitch_deck_name: response.data.filename
                          });
                        } catch (err) {
                          alert('Failed to upload pitch deck');
                        }
                        e.target.value = '';
                      }}
                    />
                    + Upload Pitch Deck
                  </label>
                )}

                {/* Other Files Section */}
                <h4 className="materials-subtitle" style={{marginTop: '24px'}}>Other Files</h4>
                {(editData.demo_files || []).map((file, idx) => (
                  <div key={idx} className="file-edit-row">
                    <span className="file-name">📄 {file.filename}</span>
                    <button 
                      type="button"
                      className="remove-link-btn"
                      onClick={() => {
                        const newFiles = [...(editData.demo_files || [])];
                        newFiles.splice(idx, 1);
                        setEditData({...editData, demo_files: newFiles});
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <label className="add-file-btn">
                  <input 
                    type="file" 
                    style={{display: 'none'}}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const response = await axios.post(`${API}/upload`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        const newFiles = [...(editData.demo_files || []), {
                          url: response.data.url,
                          filename: response.data.filename
                        }];
                        setEditData({...editData, demo_files: newFiles});
                      } catch (err) {
                        alert('Failed to upload file');
                      }
                      e.target.value = '';
                    }}
                  />
                  + Add File
                </label>

                {/* Links Section */}
                <h4 className="materials-subtitle" style={{marginTop: '24px'}}>Links</h4>
                {(editData.demo_links || []).map((link, idx) => (
                  <div key={idx} className="link-edit-row">
                    <input 
                      type="text" 
                      className="edit-input"
                      value={link || ''}
                      onChange={(e) => {
                        const newLinks = [...(editData.demo_links || [])];
                        newLinks[idx] = e.target.value;
                        setEditData({...editData, demo_links: newLinks});
                      }}
                      placeholder="https://..."
                    />
                    <button 
                      type="button"
                      className="remove-link-btn"
                      onClick={() => {
                        const newLinks = [...(editData.demo_links || [])];
                        newLinks.splice(idx, 1);
                        setEditData({...editData, demo_links: newLinks});
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button 
                  type="button"
                  className="add-link-btn"
                  onClick={() => {
                    const newLinks = [...(editData.demo_links || []), ''];
                    setEditData({...editData, demo_links: newLinks});
                  }}
                >
                  + Add Link
                </button>
              </div>
            ) : (
              <div className="materials-view">
                {/* Pitch Deck */}
                {application.pitch_deck_url && (
                  <div className="materials-list">
                    <h4 className="materials-subtitle">Pitch Deck</h4>
                    <a href={`${BACKEND_URL}${application.pitch_deck_url}`} target="_blank" rel="noopener noreferrer" className="material-item pitch-deck-item">
                      📊 {application.pitch_deck_name}
                    </a>
                  </div>
                )}
                
                {/* Other Files */}
                {application.demo_files && application.demo_files.length > 0 && (
                  <div className="materials-list">
                    <h4 className="materials-subtitle">Files</h4>
                    {application.demo_files.map((file, idx) => (
                      <a key={idx} href={`${BACKEND_URL}${file.url}`} target="_blank" rel="noopener noreferrer" className="material-item">
                        📄 {file.filename}
                      </a>
                    ))}
                  </div>
                )}
                
                {/* Links */}
                {application.demo_links && application.demo_links.filter(l => l && l.trim()).length > 0 && (
                  <div className="materials-list">
                    <h4 className="materials-subtitle">Links</h4>
                    {application.demo_links.filter(l => l && l.trim()).map((link, idx) => (
                      <a key={idx} href={link} target="_blank" rel="noopener noreferrer" className="material-item link-item">
                        🔗 {link}
                      </a>
                    ))}
                  </div>
                )}
                
                {/* No materials message */}
                {!application.pitch_deck_url && 
                 (!application.demo_links || application.demo_links.filter(l => l && l.trim()).length === 0) && 
                 (!application.demo_files || application.demo_files.length === 0) && (
                  <p className="no-materials">No supporting materials attached</p>
                )}
              </div>
            )}
          </div>

          {/* Internal Resources Section */}
          <div className="detail-section internal-resources-section">
            <h3>Internal Resources</h3>
            
            {/* Add Resource Form */}
            {canEdit && (
              <div className="add-resource-form">
                <div className="resource-form-row">
                  <select 
                    value={newResourceType} 
                    onChange={(e) => {
                      setNewResourceType(e.target.value);
                      setNewResourceName("");
                      setNewResourceUrl("");
                      setSelectedFile(null);
                    }}
                    className="resource-type-select"
                  >
                    <option value="link">Link</option>
                    <option value="file">File</option>
                  </select>
                  
                  {newResourceType === 'link' ? (
                    <>
                      <input 
                        type="text" 
                        placeholder="Resource name" 
                        value={newResourceName}
                        onChange={(e) => setNewResourceName(e.target.value)}
                        className="resource-name-input"
                      />
                      <input 
                        type="text" 
                        placeholder="https://..."
                        value={newResourceUrl}
                        onChange={(e) => setNewResourceUrl(e.target.value)}
                        className="resource-url-input"
                      />
                      <button 
                        onClick={addInternalResource}
                        disabled={addingResource || !newResourceName.trim() || !newResourceUrl.trim()}
                        className="add-resource-btn"
                      >
                        {addingResource ? "Adding..." : "+ Add Link"}
                      </button>
                    </>
                  ) : (
                    <>
                      <input 
                        type="text" 
                        placeholder="Resource name" 
                        value={newResourceName}
                        onChange={(e) => setNewResourceName(e.target.value)}
                        className="resource-name-input"
                      />
                      <input 
                        type="file"
                        id="resource-file-input"
                        onChange={(e) => setSelectedFile(e.target.files[0])}
                        className="resource-file-input"
                      />
                      <button 
                        onClick={uploadInternalFile}
                        disabled={addingResource || !selectedFile}
                        className="add-resource-btn"
                      >
                        {addingResource ? (uploadProgress > 0 ? `Uploading ${uploadProgress}%` : "Uploading...") : "+ Upload File"}
                      </button>
                      {addingResource && uploadProgress > 0 && (
                        <div className="upload-progress-bar">
                          <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Resources List */}
            <div className="resources-list">
              {(!application.internal_resources || application.internal_resources.length === 0) ? (
                <p className="no-resources">No internal resources added yet</p>
              ) : (
                application.internal_resources.map((resource) => (
                  <div key={resource.id} className="resource-item">
                    <span className="resource-icon">{resource.type === 'link' ? '🔗' : '📄'}</span>
                    <a href={resource.url} target="_blank" rel="noopener noreferrer" className="resource-link">
                      {resource.name}
                    </a>
                    <span className="resource-meta">Added by {resource.added_by}</span>
                    {canEdit && (
                      <button 
                        onClick={() => deleteInternalResource(resource.id)}
                        className="resource-delete-btn"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Comments Section */}
          <div className="detail-section comments-section">
            <h3>TEAM COMMENTS ({comments.length})</h3>
            
            {canComment && (
              <div className="add-comment">
                <textarea
                  className="comment-input"
                  placeholder="Add a note or comment about this deal..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={3}
                  data-testid="comment-input"
                />
                <button 
                  className="add-comment-btn"
                  onClick={addComment}
                  disabled={addingComment || !newComment.trim()}
                  data-testid="add-comment-btn"
                >
                  {addingComment ? "Adding..." : "Add Comment"}
                </button>
              </div>
            )}

            <div className="comments-list">
              {comments.map((comment, idx) => (
                <div key={idx} className="comment-item" data-testid={`comment-${idx}`}>
                  <div className="comment-header">
                    <span className="comment-author">{comment.author_name}</span>
                    <span className="comment-date">{formatDateTime(comment.created_at)}</span>
                    {(comment.author_id === user?.id || user?.role === 'admin') && (
                      <div className="comment-actions">
                        <button 
                          className="comment-edit-btn"
                          onClick={() => { setEditingCommentId(comment.id); setEditingCommentText(comment.text); }}
                          data-testid={`edit-comment-${idx}`}
                        >
                          ✏️
                        </button>
                        <button 
                          className="comment-delete-btn"
                          onClick={() => deleteComment(comment.id)}
                          data-testid={`delete-comment-${idx}`}
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>
                  {editingCommentId === comment.id ? (
                    <div className="edit-comment-form">
                      <textarea
                        className="comment-input"
                        value={editingCommentText}
                        onChange={(e) => setEditingCommentText(e.target.value)}
                        rows={2}
                      />
                      <div className="edit-comment-actions">
                        <button className="save-comment-btn" onClick={() => updateComment(comment.id)}>Save</button>
                        <button className="cancel-comment-btn" onClick={() => setEditingCommentId(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <p className="comment-text">{comment.text}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Admin Delete Option */}
          {canDelete && (
            <div className="danger-zone">
              <button 
                className="delete-application-btn"
                onClick={deleteApplication}
                disabled={deleting}
                data-testid="delete-application-btn"
              >
                {deleting ? "Deleting..." : "Delete this application"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== TEAM ACCESS ==============
const TeamAccess = () => {
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const [members, setMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newMember, setNewMember] = useState({ name: "", email: "", password: "", role: "viewer" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
      return;
    }
    fetchMembers();
  }, [token]);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        navigate("/dashboard/login");
      }
    }
    setLoading(false);
  };

  const addMember = async () => {
    if (!newMember.name || !newMember.email || !newMember.password) {
      alert("Please fill all fields");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/team`, newMember, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      setNewMember({ name: "", email: "", password: "", role: "viewer" });
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to add member");
    }
    setSaving(false);
  };

  const removeMember = async (memberId) => {
    if (!window.confirm("Are you sure you want to remove this team member?")) return;
    
    try {
      await axios.delete(`${API}/team/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to remove member");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) return <div className="dashboard-loading">Loading...</div>;

  return (
    <div className="team-access" data-testid="team-access">
      <div className="team-header">
        <button className="back-btn" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard">
          ← Team Access
        </button>
      </div>

      <div className="team-content">
        <p className="team-description">
          Manage who has access to the investor dashboard. Add team members who should be able to view and manage applications.
        </p>

        {user?.role === 'admin' && (
          <button 
            className="add-member-btn"
            onClick={() => setShowModal(true)}
            data-testid="add-member-btn"
          >
            👥 Add Team Member
          </button>
        )}

        <div className="members-section">
          <h3 className="members-title">👥 Team Members ({members.length})</h3>
          
          <div className="members-list">
            {members.map(member => (
              <div key={member.id} className="member-card" data-testid={`member-${member.email}`}>
                <div className="member-avatar">
                  {member.name.charAt(0).toUpperCase()}
                </div>
                <div className="member-info">
                  <div className="member-name-row">
                    <span className="member-name">{member.name}</span>
                    <span className={`role-badge role-${member.role}`}>
                      {member.role === 'admin' ? 'Admin' : member.role === 'editor' ? 'Editor' : 'Viewer'}
                    </span>
                  </div>
                  <span className="member-email">{member.email}</span>
                  <span className="member-date">Added {formatDate(member.created_at)}</span>
                </div>
                {user?.role === 'admin' && member.role !== 'admin' && (
                  <button 
                    className="remove-member-btn"
                    onClick={() => removeMember(member.id)}
                    data-testid={`remove-${member.email}`}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Add Team Member</h2>
            <input
              type="text"
              placeholder="Name"
              value={newMember.name}
              onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
              className="modal-input"
              data-testid="new-member-name"
            />
            <input
              type="email"
              placeholder="Email"
              value={newMember.email}
              onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
              className="modal-input"
              data-testid="new-member-email"
            />
            <input
              type="password"
              placeholder="Password"
              value={newMember.password}
              onChange={(e) => setNewMember(prev => ({ ...prev, password: e.target.value }))}
              className="modal-input"
              data-testid="new-member-password"
            />
            <div className="role-selection">
              <label className="role-label">Role:</label>
              <div className="role-options">
                <label className={`role-option ${newMember.role === 'editor' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="role"
                    value="editor"
                    checked={newMember.role === 'editor'}
                    onChange={(e) => setNewMember(prev => ({ ...prev, role: e.target.value }))}
                  />
                  <span className="role-option-content">
                    <strong>Editor</strong>
                    <small>Can edit deals, change status, add comments</small>
                  </span>
                </label>
                <label className={`role-option ${newMember.role === 'viewer' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="role"
                    value="viewer"
                    checked={newMember.role === 'viewer'}
                    onChange={(e) => setNewMember(prev => ({ ...prev, role: e.target.value }))}
                  />
                  <span className="role-option-content">
                    <strong>Viewer</strong>
                    <small>View only access, no editing</small>
                  </span>
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button 
                className="modal-save" 
                onClick={addMember}
                disabled={saving}
                data-testid="save-member-btn"
              >
                {saving ? "Adding..." : "Add Member"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== APP ==============
function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/apply" element={<ApplicationForm />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/login" element={<DashboardLogin />} />
            <Route path="/dashboard/login" element={<DashboardLogin />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/deal/:id" element={<DealDetail />} />
            <Route path="/dashboard/team" element={<TeamAccess />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
